import type {
	FeedItem,
	MarkdownContentBlock,
	TextContentBlock,
} from "@knocklabs/client";
import checkCircleIcon from "@ms/check_circle.svg?raw";
import errorIcon from "@ms/error.svg?raw";
import infoIcon from "@ms/info.svg?raw";
import { Alert } from "@ui/Alert";
import { Button } from "@ui/Button";
import { Card } from "@ui/Card";
import { Icon } from "@ui/Icon";
import warningCircleIcon from "@ui/icons/warningCircle.svg?raw";
import { ListTitle } from "@ui/ListTitle";
import { Loader } from "@ui/Loader";
import { NOTIFICATION_ADDED_EVENT } from "@ui/molecules/Toasts/toastStore";
import { Panel } from "@ui/Panel";
import { Search } from "@ui/Search";
import { Stack } from "@ui/Stack";
import { createInfiniteScroll } from "@utils/createInfiniteScroll";
import {
	createEffect,
	createMemo,
	createSignal,
	For,
	Match,
	on,
	onCleanup,
	Show,
	Switch,
} from "solid-js";
import type { NotificationListItem } from "@/client";
import { listNotifications } from "@/client/sdk.gen";
import {
	canUndoGroupNotification,
	groupDeleteNotificationDescription,
	restoreGroupFromRevertAction,
} from "~/modules/agencyhub/groups/undoableGroupDelete";
import { timeAgo } from "~/modules/shared/utils/time";
import { useSearch } from "~/modules/shared/utils/usePagination";
import { createKnockFeed, type KnockFeedAuth } from "./createKnockFeed";
import {
	groupMergedEntriesByDay,
	type MergedNotificationEntry,
	mergeNotificationEntries,
} from "./mergedNotifications";

/**
 * The notification list and Knock feed behind every NotificationsButton on a
 * page. Create it once and share it: each call makes its own list request and
 * its own Knock websocket.
 */
export function createNotifications(knockAuth: () => KnockFeedAuth | undefined) {
	const { search, searchInput, handleSearch, handleClear } = useSearch({});

	const [
		legacyNotifications,
		setEl,
		{ end, setEnd, reset },
		{ loading, error },
	] = createInfiniteScroll(async (page) => {
		const res = await listNotifications({
			query: {
				page: page,
				page_size: 25,
				search: search(),
			},
		});
		if (res.error) {
			return [];
		}
		if (res.data.page === res.data.total) {
			setEnd(true);
		}
		return res.data.notifications ?? [];
	}, 1);

	// Reset when search changes (defer: true skips the initial run)
	createEffect(
		on(
			search,
			() => {
				reset();
			},
			{ defer: true },
		),
	);

	const onNotificationAdded = () => reset();
	window.addEventListener(NOTIFICATION_ADDED_EVENT, onNotificationAdded);
	onCleanup(() =>
		window.removeEventListener(NOTIFICATION_ADDED_EVENT, onNotificationAdded),
	);

	// Knock covers a disjoint set of event types from the legacy table (see
	// mergedNotifications.ts) - it has no equivalent of the legacy system's
	// read/unread state, so the badge only ever reflects Knock's count.
	const knockFeed = createKnockFeed(knockAuth);

	const mergedGroups = createMemo(() =>
		groupMergedEntriesByDay(
			mergeNotificationEntries(legacyNotifications() ?? [], knockFeed.items()),
		),
	);

	return {
		searchInput,
		handleSearch,
		handleClear,
		setEl,
		end,
		reset,
		loading,
		error,
		legacyNotifications,
		knockFeed,
		mergedGroups,
	};
}

export type Notifications = ReturnType<typeof createNotifications>;

export const NotificationsButton = (props: { notifications: Notifications }) => {
	const {
		searchInput,
		handleSearch,
		handleClear,
		setEl,
		end,
		reset,
		loading,
		error,
		legacyNotifications,
		knockFeed,
		mergedGroups,
	} = props.notifications;
	const hasNewNotifications = () => knockFeed.unreadCount() > 0;

	const [isOpen, setIsOpen] = createSignal(false);
	return (
		<>
			<Button
				class={`notifications-button ${hasNewNotifications() ? "has-new-notifications" : ""}`}
				layout="icon"
				shape="circle"
				variant="text"
				theme="monochrome"
				aria-label="Notifications"
				symbol="notifications"
				symbolRelativeSize="even-larger"
				onClick={() => {
					setIsOpen(!isOpen());
				}}
			>
				{hasNewNotifications() && <span class="new-notifications-badge"></span>}
			</Button>
			<Panel
				isOpen={isOpen}
				class="notifications-panel"
				onToggle={() => {
					setIsOpen(!isOpen());
				}}
				title="Notifications"
				symbol="notifications"
			>
				<Stack spacing="large" direction="column">
					<Show when={knockFeed.items().length > 0}>
						<Button
							variant="secondary"
							theme="monochrome"
							onClick={() => knockFeed.markAllAsRead()}
						>
							Mark all as read
						</Button>
					</Show>
					<Search
						value={searchInput()}
						onSearch={handleSearch}
						onClear={handleClear}
					/>
					<Switch>
						<Match when={loading()}>
							<div />
						</Match>
						<Match when={error()}>
							<Alert variant="error">Error: {error()}</Alert>
						</Match>
						<Match when={legacyNotifications()}>
							<Stack spacing="medium" direction="column">
								{Object.entries(mergedGroups()).map(([date, entries]) => (
									<NotificationSection
										date={date}
										entries={entries}
										onRevert={() => reset()}
										onMarkAsRead={(item) => knockFeed.markAsRead(item)}
									/>
								))}
							</Stack>
						</Match>
					</Switch>
					<Show when={!end()}>
						<div ref={setEl}>
							<Loader />
						</div>
					</Show>
				</Stack>
			</Panel>
		</>
	);
};

const NotificationSection = (props: {
	date: string;
	entries: MergedNotificationEntry[];
	onRevert?: () => void;
	onMarkAsRead: (item: FeedItem) => void;
}) => {
	return (
		<Stack spacing="medium" direction="column">
			<ListTitle>{timeAgo(props.date)}</ListTitle>
			<For each={props.entries}>
				{(entry) => (
					<Switch>
						<Match when={entry.kind === "legacy" && entry}>
							{(legacyEntry) => (
								<LegacyNotificationCard
									notification={legacyEntry().item}
									onRevert={props.onRevert}
								/>
							)}
						</Match>
						<Match when={entry.kind === "knock" && entry}>
							{(knockEntry) => (
								<KnockNotificationCard
									item={knockEntry().item}
									onMarkAsRead={() => props.onMarkAsRead(knockEntry().item)}
								/>
							)}
						</Match>
					</Switch>
				)}
			</For>
		</Stack>
	);
};

const LegacyNotificationCard = (props: {
	notification: NotificationListItem;
	onRevert?: () => void;
}) => {
	const showUndo = () =>
		props.notification.status === "active" &&
		canUndoGroupNotification(props.notification.revert_action);
	const notificationDescription = () => {
		if (canUndoGroupNotification(props.notification.revert_action)) {
			return groupDeleteNotificationDescription(props.notification);
		}
		return props.notification?.description ?? "";
	};
	const undoLabel = () =>
		canUndoGroupNotification(props.notification.revert_action)
			? "Restore"
			: "Undo";
	return (
		<Card>
			<Stack spacing="small" direction="row" align="center">
				<NotificationIcon type={props.notification.type} />
				<div class="title">{props.notification.title}</div>
			</Stack>
			<div class="description">{notificationDescription()}</div>
			<Show when={showUndo()}>
				<Button
					variant="secondary"
					theme="monochrome"
					onClick={async () => {
						const ok = canUndoGroupNotification(
							props.notification.revert_action,
						)
							? await restoreGroupFromRevertAction(
								props.notification.revert_action,
								{ onRestored: props.onRevert },
							)
							: false;
						if (ok) {
							props.onRevert?.();
						}
					}}
				>
					{undoLabel()}
				</Button>
			</Show>
		</Card>
	);
};

const KnockNotificationCard = (props: {
	item: FeedItem;
	onMarkAsRead: () => void;
}) => {
	const body = () =>
		props.item.blocks.find(
			(b): b is MarkdownContentBlock | TextContentBlock =>
				b.name === "body" && b.type !== "button_set",
		);
	return (
		<Card
			class={props.item.read_at ? "notification-read" : "notification-unread"}
		>
			{/* The body is Knock's own markup, so it can carry links of its own.
			    Marking as read is an explicit control beside it, rather than a
			    click on the row that would compete with them. */}
			<Show when={body()}>
				<div class="description" innerHTML={body()?.rendered ?? ""} />
			</Show>
			<div class="timestamp">{timeAgo(props.item.inserted_at)}</div>
			<Show when={!props.item.read_at}>
				<Button
					variant="secondary"
					theme="monochrome"
					onClick={props.onMarkAsRead}
				>
					Mark as read
				</Button>
			</Show>
		</Card>
	);
};

const NotificationIcon = (props: { type: NotificationListItem["type"] }) => {
	const statusIcons = new Map<NotificationListItem["type"], string>([
		["success", checkCircleIcon],
		["warning", warningCircleIcon],
		["error", errorIcon],
		["info", infoIcon],
	]);
	const statusColors = new Map<NotificationListItem["type"], string>([
		["success", "var(--gvt-green)"],
		["warning", "var(--gvt-warning)"],
		["error", "var(--gvt-error)"],
		["info", "var(--gvt-primary)"],
	]);
	return (
		<Stack
			align="center"
			justify="center"
			style={{ color: statusColors.get(props.type) ?? "var(--grey-600)" }}
		>
			<Icon class="notification-icon" icon={statusIcons.get(props.type)} />
		</Stack>
	);
};
